import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/CreateSnippetPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member {
  user_id: number;
  username: string;
  display_name?: string;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Note {
  note_id: number;
  note: string;
  created_at: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

const CreateSnippetPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [enrolledCurriculums, setEnrolledCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<number | null>(null);
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedNotes, setSelectedNotes] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [curriculumError, setCurriculumError] = useState<string>("");
  const [noteError, setNoteError] = useState<string>("");
  const [showProcessingDialog, setShowProcessingDialog] = useState<boolean>(false);

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);

    setPeriodEnd(today.toISOString().split('T')[0]);
    setPeriodStart(oneWeekAgo.toISOString().split('T')[0]);
  }, []);

  // Fetch member info
  useEffect(() => {
    const fetchMemberInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const data = await response.json();
        setMemberInfo(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberInfo();
    }
  }, [member_id, token, selectedOrgID]);

  // Fetch enrolled curriculums
  useEffect(() => {
    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data: Curriculum[] = await response.json();
        setEnrolledCurriculums(Array.isArray(data) ? data : []);
        
        // If there's only one curriculum, select it by default
        if (data.length === 1) {
          setSelectedCurriculum(data[0].curriculum_id);
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchCurriculums();
    }
  }, [member_id, token, selectedOrgID]);

  // Fetch notes whenever date range changes
  useEffect(() => {
    const fetchNotes = async () => {
      if (!periodStart || !periodEnd) return;

      // Clear any previous date errors
      setDateError("");

      // Validate that end date is after start date
      const startDate = new Date(periodStart);
      const endDate = new Date(periodEnd);
      
      if (endDate < startDate) {
        setDateError("End date must be after start date");
        setNotes([]);
        setSelectedNotes(new Set());
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/notes?user_id=${member_id}&start_date=${periodStart}&end_date=${periodEnd}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch notes.");
          return;
        }

        const data = await response.json();
        const notesList = Array.isArray(data) ? data : [];
        setNotes(notesList);
        
        // Select all notes by default
        setSelectedNotes(new Set(notesList.map(note => note.note_id)));
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchNotes();
  }, [member_id, periodStart, periodEnd, token, selectedOrgID]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError("");
    setDateError("");
    setCurriculumError("");
    setNoteError("");
    
    let hasError = false;

    if (!selectedCurriculum) {
      setCurriculumError("Please select a curriculum.");
      hasError = true;
    }

    // Validate that end date is after start date
    const startDate = new Date(periodStart);
    const endDate = new Date(periodEnd);
    
    if (endDate < startDate) {
      setDateError("End date must be after start date");
      hasError = true;
      // Return early if date validation fails
      return;
    }

    // Only check for note selection if date validation passes
    if (selectedNotes.size === 0) {
      setNoteError("Please select at least one note.");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    // Convert dates to RFC3339 format with appropriate times and timezone
    const startDateTime = new Date(periodStart);
    startDateTime.setHours(0, 0, 0, 0);
    
    const endDateTime = new Date(periodEnd);
    endDateTime.setHours(23, 59, 59, 999);

    // Get timezone offset in hours:minutes format
    const offsetMinutes = startDateTime.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60)).toString().padStart(2, "0");
    const offsetMins = Math.abs(offsetMinutes % 60).toString().padStart(2, "0");
    const offsetSign = offsetMinutes <= 0 ? "+" : "-";

    // Format dates with timezone, ensuring we use the selected dates
    const formattedStartDate = `${periodStart}T00:00:00${offsetSign}${offsetHours}:${offsetMins}`;
    const formattedEndDate = `${periodEnd}T23:59:59${offsetSign}${offsetHours}:${offsetMins}`;

    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          user_id: parseInt(member_id || "0", 10),
          curriculum_id: selectedCurriculum,
          period_start: formattedStartDate,
          period_end: formattedEndDate,
          note_ids: Array.from(selectedNotes).join(","),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          setShowProcessingDialog(true);
        } else {
          setError(errorData.error || "Failed to create snippet.");
        }
        return;
      }

      // Navigate back to snippets page
      navigate(`/members/${member_id}/snippets`);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleNoteToggle = (noteId: number) => {
    setSelectedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Create Snippet</h1>
      {memberInfo && (
        <p className={styles.memberInfo}>
          <strong>Member:</strong>{" "}
          <a href={`/members/${member_id}`} className={styles.memberLink}>
            {memberInfo.display_name 
              ? `${memberInfo.display_name} (${memberInfo.username})`
              : memberInfo.username}
          </a>
          {" / "}
          <a href={`/members/${member_id}/snippets`} className={styles.memberLink}>Snippets</a>
        </p>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Curriculum:</label>
          <select
            value={selectedCurriculum?.toString() || ""}
            onChange={(e) => {
              setSelectedCurriculum(parseInt(e.target.value, 10));
              setError("");
              setCurriculumError("");
            }}
            required
            className={styles.select}
          >
            <option value="" disabled>Select a curriculum</option>
            {enrolledCurriculums.map((c) => (
              <option key={c.curriculum_id} value={c.curriculum_id}>{c.name}</option>
            ))}
          </select>
          {!selectedCurriculum && curriculumError && (
            <p className={styles.fieldError}>{curriculumError}</p>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Period Start:</label>
          <input
            type="date"
            value={periodStart}
            onChange={(e) => {
              setPeriodStart(e.target.value);
              setDateError("");
              if (error) setError("");
            }}
            required
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Period End:</label>
          <input
            type="date"
            value={periodEnd}
            onChange={(e) => {
              setPeriodEnd(e.target.value);
              setDateError("");
              if (error) setError("");
            }}
            required
            className={styles.input}
          />
        </div>

        {dateError && (
          <p className={styles.fieldError}>{dateError}</p>
        )}

        {selectedNotes.size === 0 && noteError && !dateError && (
          <p className={styles.fieldError}>{noteError}</p>
        )}
        
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Created</th>
              <th className={styles.tableHeader}>Note</th>
              <th className={styles.tableHeader}>Include</th>
            </tr>
          </thead>
          <tbody>
            {notes.length > 0 ? (
              notes.map((note) => (
                <tr key={note.note_id}>
                  <td className={styles.tableCell}>{new Date(note.created_at).toLocaleDateString()}</td>
                  <td className={styles.tableCell}>{note.note}</td>
                  <td className={styles.tableCell}>
                    <input
                      type="checkbox"
                      checked={selectedNotes.has(note.note_id)}
                      onChange={() => {
                        handleNoteToggle(note.note_id);
                        if (noteError) {
                          setNoteError("");
                        }
                      }}
                      className={styles.checkbox}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className={styles.tableCell}>No notes found for the selected period.</td>
              </tr>
            )}
          </tbody>
        </table>

        <button type="submit" className={styles.submitButton}>Create Snippet</button>
      </form>

      {showProcessingDialog && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setShowProcessingDialog(false)} />
          <div className={styles.dialog}>
            <p>
              We are still processing another snippet for the same member.<br /><br />
              Please try again once that completes.
            </p>
            <div className={styles.dialogButtons}>
              <button className={styles.dialogButton} onClick={() => setShowProcessingDialog(false)}>OK</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateSnippetPage; 